import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, Phone, Instagram, MapPin, Award, Heart, Globe2, Brain
} from 'lucide-react';

/**
 * Priya Khurana Recommendation Card.
 *
 * Renders below AuthorBox on Tarot / Love / Relationship / Marriage /
 * Compatibility / Emotional Healing blog posts to drive consultation leads.
 *
 * Conversion events (WhatsApp Click, Book Click, Profile Visit) are tracked
 * via gtag (if present) and POSTed to backend `/api/track/event` (best-effort).
 */

const API = process.env.REACT_APP_BACKEND_URL;
const PRIYA_PHONE_INTL = '971508238898';
const PRIYA_INSTA = 'iamtarotperspective';
const PRIYA_PHOTO = '/experts/priya-khurana-card.png';
const PRIYA_PROFILE = '/experts/priya-khurana';

function trackEvent(eventName, extra = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, { event_category: 'priya_card', ...extra });
    }
  } catch { /* ignore */ }
  // Best-effort backend ping (silent)
  try {
    fetch(`${API}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: eventName, source: 'priya_recommendation_card', ...extra }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

const FAQ_ITEMS = [
  {
    q: 'How does online Tarot consultation work?',
    a: 'Online sessions happen over Zoom, Google Meet or WhatsApp video call. Priya guides you through a structured 45–60 minute session — she shuffles the deck, draws a spread based on your specific question, and walks you through emotional patterns and insights revealed by the cards. Sessions are confidential and recorded only on your request.',
  },
  {
    q: 'Can international clients book sessions?',
    a: 'Yes. Priya regularly consults with clients across UAE, India, Canada, Singapore, UK, USA and beyond. She accommodates different timezones (typically GST, IST, EST, PST, GMT). Payment is supported in multiple currencies — AED, INR, USD, GBP, EUR.',
  },
  {
    q: 'Is Tarot useful for relationship guidance?',
    a: 'Absolutely. Tarot, when combined with relationship coaching frameworks (attachment theory, emotional regulation), becomes a powerful self-awareness tool. Priya does not predict if a relationship will "work" or "fail" — she helps you understand your patterns, communication style, emotional triggers and the dynamics at play, so you can make conscious decisions.',
  },
  {
    q: 'How do I book a consultation?',
    a: 'The fastest way is WhatsApp +971 50 823 8898 — Priya personally responds within 24 hours. Alternatively, visit her full profile page and submit the booking form with your preferred time and topic. Email follow-ups are sent for time-confirmation.',
  },
];

export default function PriyaRecommendationCard() {
  const whatsappUrl =
    `https://wa.me/${PRIYA_PHONE_INTL}?text=${encodeURIComponent(
      'Namaste Priya, I just read a blog on ViratAstro and would like to book a personal Tarot / Relationship consultation.',
    )}`;

  return (
    <section
      className="my-10 rounded-3xl bg-gradient-to-br from-bg-card via-bg-secondary to-bg-primary border border-gold/30 overflow-hidden"
      data-testid="priya-recommendation-card"
      itemScope itemType="https://schema.org/Person"
    >
      <meta itemProp="@id" content="https://viratastro.cloud/experts/priya-khurana#person" />
      <meta itemProp="url"  content="https://viratastro.cloud/experts/priya-khurana" />

      {/* Tag-line strip */}
      <div className="px-5 sm:px-7 pt-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-gold" />
          <span className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-gold font-bold">Need Personal Guidance?</span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary leading-tight">
          Book a personal session with Priya Khurana
        </h3>
        <p className="text-xs text-text-muted mt-1">Featured Tarot Expert at ViratAstro · Dubai · Confidential one-to-one</p>
      </div>

      <div className="grid sm:grid-cols-[140px_1fr] gap-5 p-5 sm:p-7 pt-4">
        {/* Photo */}
        <Link
          to={PRIYA_PROFILE}
          onClick={() => trackEvent('priya_profile_visit_from_card')}
          className="block sm:w-32 sm:h-32 w-24 h-24 mx-auto sm:mx-0 rounded-full bg-gradient-to-br from-gold/30 to-purple-500/20 border-2 border-gold/50 overflow-hidden hover:scale-105 transition"
          data-testid="priya-card-photo-link"
        >
          <img
            src={PRIYA_PHOTO}
            alt="Priya Khurana — Dubai-based Tarot Teacher and Relationship Coach"
            className="w-full h-full object-cover"
            itemProp="image"
            loading="lazy"
          />
        </Link>

        {/* Body */}
        <div>
          <Link to={PRIYA_PROFILE} onClick={() => trackEvent('priya_profile_visit_from_card')} className="hover:text-gold transition">
            <h4 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" itemProp="name">
              Priya Khurana
            </h4>
          </Link>
          <p className="text-[12px] sm:text-sm text-gold mt-0.5 mb-3" itemProp="jobTitle">
            Dubai-Based Tarot Teacher &amp; Relationship Coach
          </p>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] sm:text-xs text-text-muted mb-3">
            <span className="flex items-center gap-1"><Award className="w-3 h-3 text-gold" /> Trained in Canada &amp; Singapore</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-gold" /> 6+ Years Experience</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> Dubai, UAE</span>
          </div>

          <ul className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-[12px] sm:text-sm text-text-muted mb-4" itemProp="knowsAbout">
            <li className="flex items-center gap-1.5"><Heart    className="w-3.5 h-3.5 text-pink-400" /> Relationship Guidance</li>
            <li className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-gold" />     Love &amp; Marriage Clarity</li>
            <li className="flex items-center gap-1.5"><Brain    className="w-3.5 h-3.5 text-purple-400" />Self-Awareness Coaching</li>
            <li className="flex items-center gap-1.5"><Globe2   className="w-3.5 h-3.5 text-blue-400" /> Online Consultations Worldwide</li>
          </ul>

          <div className="flex flex-wrap gap-2 mb-3">
            <Link
              to={PRIYA_PROFILE}
              onClick={() => trackEvent('priya_book_consultation_click')}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gold text-bg-primary text-sm font-semibold hover:bg-yellow-400 transition shadow-lg shadow-yellow-900/20"
              data-testid="priya-card-book"
            >
              Book Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent('priya_whatsapp_click', { number: PRIYA_PHONE_INTL })}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition"
              data-testid="priya-card-whatsapp"
            >
              <Phone className="w-3.5 h-3.5" /> WhatsApp Now
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-[11px] sm:text-xs text-text-muted">
            <a
              href={whatsappUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent('priya_whatsapp_click_secondary')}
              className="flex items-center gap-1 hover:text-green-400 transition"
              itemProp="telephone"
            >
              <Phone className="w-3 h-3" /> +971 50 823 8898
            </a>
            <span className="text-gold/30">·</span>
            <a
              href={`https://instagram.com/${PRIYA_INSTA}`}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent('priya_instagram_click')}
              className="flex items-center gap-1 hover:text-pink-400 transition"
              itemProp="sameAs"
            >
              <Instagram className="w-3 h-3" /> @{PRIYA_INSTA}
            </a>
          </div>
        </div>
      </div>

      {/* FAQ accordion */}
      <div className="px-5 sm:px-7 pb-6 pt-2 border-t border-gold/15">
        <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-gold/80 font-bold mb-3 mt-4">Common Questions</p>
        <div className="space-y-2">
          {FAQ_ITEMS.map(({ q, a }, i) => (
            <details key={i} className="group rounded-xl bg-bg-primary/50 border border-gold/15 p-3.5 hover:border-gold/35 transition" data-testid={`priya-card-faq-${i}`}>
              <summary className="cursor-pointer list-none flex justify-between items-center gap-3 text-sm font-medium text-text-primary">
                <span>{q}</span>
                <span className="text-gold text-lg transition-transform group-open:rotate-45 flex-shrink-0">+</span>
              </summary>
              <p className="mt-2.5 text-[13px] sm:text-sm text-text-muted leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// Category / keyword detection helper — exported so BlogDetailPage can decide
// whether to render the card without duplicating regex logic.
const _RELATIONSHIP_CATS = new Set([
  'tarot', 'love', 'relationship', 'relationships', 'marriage',
  'compatibility', 'emotional', 'emotional-healing', 'self-awareness',
  'romance', 'partner', 'dating',
]);
const _RELATIONSHIP_KEYWORDS = /\b(tarot|love|relationship|romantic|marriage|compatib|partner|dating|attachment|emotional|breakup|divorce|heartbreak|self.?awareness|self.?discovery|प्रेम|शादी|विवाह|टैरो|रिश्ता|रिश्ते|दांपत्य)\b/i;

export function isPriyaRecommendableBlog(blog = {}) {
  const cat = (blog.category || '').toLowerCase().trim();
  if (_RELATIONSHIP_CATS.has(cat)) return true;
  const text = `${blog.title || ''} ${blog.excerpt || ''} ${blog.keywords || ''}`;
  return _RELATIONSHIP_KEYWORDS.test(text);
}
